import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useKdsTokens } from '../../theme/kdsTokens';

interface KDSTimerProps {
    iniciadoEm?: string;
    disparadoEm?: string;
    criadoEm: string;
    tempoEstimadoSegundos: number;
    tempoAlertaAmareloSegundos?: number;
    tempoAlertaVermelhoSegundos?: number;
    /** `pilula` = cronômetro com fundo próprio, no canto do card. */
    variante?: 'pilula' | 'texto';
}

const formatarTempo = (segundos: number): string => {
    const min = Math.floor(segundos / 60);
    const sec = segundos % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

/**
 * Cronômetro do pedido na estação.
 *
 * Em pílula, e não em texto solto: numa tela de cozinha vista a dois metros, o
 * tempo é a informação que decide a ordem do trabalho — precisa ter área de cor
 * suficiente para ser lida de relance. A cor vem dos alertas configurados na
 * estação e sai da paleta do tema (white-label), não de um hexadecimal fixo.
 */
export const KDSTimer: React.FC<KDSTimerProps> = ({
    iniciadoEm,
    disparadoEm,
    criadoEm,
    tempoEstimadoSegundos,
    tempoAlertaAmareloSegundos = 300,
    tempoAlertaVermelhoSegundos = 600,
    variante = 'pilula',
}) => {
    const tokens = useKdsTokens();
    const [segundosDecorridos, setSegundosDecorridos] = useState(0);

    useEffect(() => {
        const referencia = iniciadoEm ?? disparadoEm ?? criadoEm;
        const calcular = () => {
            const diff = Math.floor((Date.now() - new Date(referencia).getTime()) / 1000);
            setSegundosDecorridos(Math.max(0, diff));
        };
        calcular();
        const interval = setInterval(calcular, 1000);
        return () => clearInterval(interval);
    }, [iniciadoEm, disparadoEm, criadoEm]);

    const cor = tokens.alertaCor(segundosDecorridos, tempoAlertaAmareloSegundos, tempoAlertaVermelhoSegundos);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Typography
                sx={{
                    color: cor,
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    // Tabular: sem isso o cronômetro "pula" a cada segundo,
                    // porque os dígitos têm larguras diferentes.
                    fontVariantNumeric: 'tabular-nums',
                    ...(variante === 'pilula' && {
                        px: 0.85,
                        py: 0.15,
                        borderRadius: 1,
                        backgroundColor: tokens.tenue(cor, 0.14),
                    }),
                }}
            >
                {formatarTempo(segundosDecorridos)}
            </Typography>
            {tempoEstimadoSegundos > 0 && (
                <Typography sx={{ color: tokens.textoFraco, fontSize: '0.72rem', fontVariantNumeric: 'tabular-nums' }}>
                    / {formatarTempo(tempoEstimadoSegundos)}
                </Typography>
            )}
        </Box>
    );
};
